import React, { useState, useEffect } from 'react';
import {
  List,
  Datagrid,
  TextField,
  NumberField,
  BooleanField,
  DateField,
  useDataProvider,
  Loading
} from 'react-admin';
import { apiClient } from '../../framework/api/api.client';

interface DynamicEntityListV2Props {
  schemaKey?: string;
  entityName: string;
}

// Column can be a simple string or a rich object with metadata
type ColumnDefinition = string | {
  field: string;
  header?: string;
  width?: number;
  sortable?: boolean;
  searchable?: boolean;
};

/**
 * Enhanced Dynamic Entity List
 * 
 * If schemaKey is provided, fetches the JSON Schema to determine which fields to display
 * Otherwise, shows generic columns (id, name, status, etc.)
 */
export const DynamicEntityListV2: React.FC<DynamicEntityListV2Props> = ({ 
  schemaKey,
  entityName 
}) => {
  const dataProvider = useDataProvider();
  const [columns, setColumns] = useState<ColumnDefinition[]>(['id']);
  const [fieldTypes, setFieldTypes] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(!!schemaKey);

  useEffect(() => {
    if (!schemaKey) {
      // Use generic columns if no schema
      setColumns(['id', 'name', 'status', 'createdAt', 'updatedAt']);
      setLoading(false);
      return;
    }

    const fetchSchemaColumns = async () => {
      try {
        setLoading(true);
        console.log(`🔍 DynamicEntityListV2: Fetching schema for: ${schemaKey}`);
        
        // Use dedicated schema-by-key endpoint (simpler and more reliable)
        const schema = await apiClient.get(`/schema/key/${schemaKey}`);
        
        if (schema) {
          // Parse JSON schema to get field types
          const jsonSchema = schema.jsonSchema ? JSON.parse(schema.jsonSchema) : null;
          const properties = jsonSchema?.properties || {};
          
          // Build field type map from JSON schema
          const typeMap: Record<string, string> = {};
          Object.entries(properties).forEach(([fieldName, fieldDef]: [string, any]) => {
            typeMap[fieldName] = fieldDef.type || 'string';
          });
          setFieldTypes(typeMap);
          
          // Check if listViewConfig is defined (admin-configured columns)
          if (schema.listViewConfig?.columns && schema.listViewConfig.columns.length > 0) {
            console.log(`✅ DynamicEntityListV2: Using admin-configured columns:`, schema.listViewConfig.columns);
            setColumns(schema.listViewConfig.columns);
          } else {
            // Auto-generate columns from JSON Schema
            const jsonSchema = JSON.parse(schema.jsonSchema);
            const properties = jsonSchema.properties || {};
            
            // Get important fields (prioritize name, title, status, email, etc.)
            const priorityFields = ['name', 'title', 'subject', 'displayName', 'label'];
            const statusFields = ['status', 'state'];
            const identifierFields = ['id', 'key', 'code', 'number', 'sku'];
            const contactFields = ['email', 'phone'];
            const dateFields = ['createdAt', 'updatedAt', 'date'];
            
            const fieldNames = Object.keys(properties).filter(f => 
              f !== '_id' && f !== 'context' && f !== 'derivedBy' && f !== 'metadata'
            );
            
            // Build column list with priority ordering
            const selectedColumns: string[] = [];
            
            // Add priority fields first
            priorityFields.forEach(pf => {
              if (fieldNames.includes(pf)) selectedColumns.push(pf);
            });
            
            // Add identifier fields
            identifierFields.forEach(idf => {
              if (fieldNames.includes(idf) && !selectedColumns.includes(idf)) {
                selectedColumns.push(idf);
              }
            });
            
            // Add status fields
            statusFields.forEach(sf => {
              if (fieldNames.includes(sf) && !selectedColumns.includes(sf)) {
                selectedColumns.push(sf);
              }
            });
            
            // Add contact fields
            contactFields.forEach(cf => {
              if (fieldNames.includes(cf) && !selectedColumns.includes(cf)) {
                selectedColumns.push(cf);
              }
            });
            
            // Add date fields
            dateFields.forEach(df => {
              if (fieldNames.includes(df) && !selectedColumns.includes(df)) {
                selectedColumns.push(df);
              }
            });
            
            // Limit to 8 columns max for readability
            setColumns(selectedColumns.slice(0, 8));
            console.log(`✅ DynamicEntityListV2: Using auto-generated columns:`, selectedColumns.slice(0, 8));
          }
        } else {
          // Fallback to generic
          setColumns(['id', 'name', 'status']);
        }
      } catch (err) {
        console.error('❌ Failed to fetch schema for list:', err);
        setColumns(['id', 'name', 'status']);
      } finally {
        setLoading(false);
      }
    };

    fetchSchemaColumns();
  }, [schemaKey, dataProvider]);

  if (loading) {
    return (
      <List title={entityName}>
        <Loading />
      </List>
    );
  }

  return (
    <List title={entityName}>
      <Datagrid rowClick="edit">
        {columns.map(col => {
          // Handle both string columns and rich column objects
          const fieldName = typeof col === 'string' ? col : col.field;
          const label = typeof col === 'string' ? formatLabel(col) : col.header || formatLabel(col.field);
          
          // Get the actual type from JSON schema (if available)
          const schemaType = fieldTypes[fieldName];
          
          // Render appropriate field component based on actual schema type
          if (schemaType === 'boolean') {
            return <BooleanField key={fieldName} source={fieldName} label={label} />;
          }
          if (schemaType === 'integer' || schemaType === 'number') {
            return <NumberField key={fieldName} source={fieldName} label={label} />;
          }
          // Only render as DateField for actual date/datetime fields from schema
          // Check field names that are definitely dates (not just containing 'time' or 'date')
          if (fieldName === 'createdAt' || 
              fieldName === 'updatedAt' ||
              fieldName === 'date' ||
              fieldName === 'invoiceDate' ||
              fieldName === 'dueDate' ||
              fieldName === 'orderDate' ||
              fieldName === 'paymentDate' ||
              fieldName === 'transactionDate' ||
              fieldName.endsWith('At') && fieldName !== 'status') {
            return <DateField key={fieldName} source={fieldName} label={label} showTime />;
          }
          // Default to TextField for strings, enums, time-only fields, and unknowns
          return <TextField key={fieldName} source={fieldName} label={label} />;
        })}
      </Datagrid>
    </List>
  );
};

/**
 * Format field name for display
 */
function formatLabel(fieldName: string): string {
  return fieldName
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, str => str.toUpperCase())
    .trim();
}

